// BrushMenu

import './BrushMenu.css';
import React, { useState, useEffect } from 'react';
import useStore from '../../../store';
import { 
  FaBrush, FaSprayCan, FaPencilAlt, FaFeatherAlt, FaPenFancy, FaPaintBrush,
  FaDotCircle, FaPaw, FaCog, FaDrawPolygon,
  FaExclamation, FaTrash, FaFolderPlus, FaPlus, FaMinus,
  FaPaintRoller,
} from 'react-icons/fa';
import { 
  FaBrush as FaBrush6, FaMarker, FaPenToSquare, FaBroomBall, FaRegLightbulb, FaBorderAll,
} from 'react-icons/fa6';
import { 
  TbPencilDown, TbLineDashed, TbBrush, TbSparkles, TbTexture, TbRosette, TbGrain,
} from "react-icons/tb";
import { useTranslation } from 'react-i18next';
import { createBetterClick } from '../../../utils/interface/createBetterClick'
import useDragAndDropSort from '../../../hooks/interface/useDragAndDropSort';
import ObjectID from 'bson-objectid';

const brushIcons = {
  'plain': FaMarker,
  'stylus': FaPenToSquare,
  'spray': FaSprayCan,
  'bristle': FaPaintBrush,
  'feather': FaFeatherAlt,
  'ink': FaPenFancy,
  'pencil': FaPencilAlt,
  'oil': FaBrush,
  'rembrandt': FaPaintRoller,
  'watercolor': TbBrush,
  'outlined': FaDotCircle,
  'dashed': TbLineDashed,
  'neon': FaRegLightbulb,
  'sparkle': TbSparkles,
  'glyph': TbRosette,
  'shape': FaDrawPolygon,
  'pixels': FaBorderAll,

  'texture': TbTexture,
  
  'test': FaPaw,
  'error': FaExclamation,
  // Новые кисти

  'wet': FaBroomBall,
  'flat': FaBrush6,
  'velvetPastel': TbGrain,
  'waterSpread': TbBrush,
  'waterSoft': TbBrush,
}

function BrushMenu({ store }) {
  const { t } = useTranslation();
  useStore(state => state.render);
  const brushType = useStore(state => state.brushType);

  const { ref } = store;
  const { menu, brush, info, work, premium } = ref;
  const { game, user } = info;

  const brushMenuRef = React.useRef(null);
  const deleteZoneRef = React.useRef(null);
  const [isOverDeleteZone, setIsOverDeleteZone] = useState(false);

  const brushesInGroupsCount = () => {
    const counts = {};
    const category = getBrushCategory();
    const allBrushes = {
      basic: allBasicBrushes,
      custom: allCustomBrushes,
      market: allMarketBrushes,
    }[category];

    const groupsKey = `${category}BrushGroups`;
    const groups = user[groupsKey] || {};

    allBrushes.forEach(brush => {
      const settings = customBrushSetsFun(brush.key);
      if (settings.groupId && groups[settings.groupId]) {
        if (!counts[settings.groupId]) {
          counts[settings.groupId] = 0;
        }
        counts[settings.groupId]++;
      }
    });
    return counts;
  };

  const {
    premiumFeatures = [],
  } = premium;

  const {
    openMenu,
    closeMenu,
    delayPremiumMenu,
    showPopupMessage,
    showTemporaryHint,
  } = menu.methods;

  const {
    saveBrush,
    saveUserSets,
    deleteBrush,
  } = work.methods;

  const {
    customBrushSetsFun,
    changeBrushType,
  } = brush.methods;

  const freeBrushes = ['liner', 'plain', 'pixels'];

  const brushItemAvailabilityStyle = (currentBrushType) => {
    if (brushType === currentBrushType) return 'active';
    if (premiumFeatures.includes('brushes')) return '';
    if (freeBrushes.includes(currentBrushType)) return '';
    return 'not-available';
  };
  // Разделение кистей на категории
  const getBrushes = () => {
    // ИЗМЕНЕНИЕ: Добавляем 'mafia' в условие ограниченного набора кистей
    if (['sprint', 'mafia'].includes(game.mode)) {
      return {
        basicBrushes: [
          'plain', 'pixels', 'feather', 'ink', 'shape', 'outlined', 'dashed'
        ].map(key => ({
          type: 'brush',
          key,
          label: t(`brush.${key}.name`),
          icon: brushIcons[key],
        })),
        customBrushes: [],
        marketBrushes: [],
        allBasicBrushes: [],
        allCustomBrushes: [],
        allMarketBrushes: [],
      };
    }

    const applySortOrder = (items, orderArray) => {
      if (!orderArray || orderArray.length === 0) return items;
      const itemMap = new Map(items.map(b => [b.key, b]));
      const sorted = orderArray.map(key => itemMap.get(key)).filter(Boolean);
      const unsorted = items.filter(b => !orderArray.includes(b.key));
      return [...sorted, ...unsorted];
    };

    const structureItems = (brushes, groupsObject = {}, brushOrder) => {
      const groupsArray = Object.entries(groupsObject).map(([id, group]) => ({
        ...group,
        key: id,
        id,
        type: 'group',
      }));

      const brushesInGroups = {};
      const brushesWithoutGroup = [];

      brushes.forEach(brushItem => {
        const brushSettings = customBrushSetsFun(brushItem.key);
        if (brushSettings.groupId && groupsObject[brushSettings.groupId]) {
          brushesInGroups[brushSettings.groupId] ??= []
          brushesInGroups[brushSettings.groupId].push(brushItem);
        } else {
          brushesWithoutGroup.push(brushItem);
        }
      });

      // Создаем единый массив для сортировки групп и кистей верхнего уровня
      const topLevelItems = [...groupsArray, ...brushesWithoutGroup];
      const sortedTopLevelItems = applySortOrder(topLevelItems, brushOrder);

      const finalStructure = [];
      sortedTopLevelItems.forEach(item => {
        if (item.type === 'group') {
          finalStructure.push(item);
          if (item.expanded) {
            const sortedBrushesInGroup = applySortOrder(brushesInGroups[item.id] || [], brushOrder);
            finalStructure.push(...sortedBrushesInGroup);
          }
        } else {
          finalStructure.push(item);
        }
      });

      return finalStructure;
    };


    const allBasicBrushes = [
      'plain', 'pixels', 'shape', 'spray', 'bristle', 'wet', 'velvetPastel', 'waterSoft', 'waterSpread', 'flat', 'feather', 'ink', 'pencil',
      'watercolor', 'rembrandt', 'dashed', 'outlined', 'neon',
      'sparkle', 'glyph', 'test'
    ].map(key => ({
      type: 'brush',
      key,
      label: t(`brush.${key}.name`),
      icon: brushIcons[key],
    }));
    const basicBrushes = structureItems(allBasicBrushes, user.basicBrushGroups, user.basicBrushOrder);

    const savedBrushes = user.savedBrushes || [];
    const allCustomBrushes = savedBrushes.map(key => {
      const brushSettings = customBrushSetsFun(key);
      const basicBrushLink = brushSettings.clone;
      if (!brushIcons[basicBrushLink]) {
        console.log(basicBrushLink);
      }
      return {
        type: 'brush',
        key,
        label: brushSettings.label,
        icon: brushIcons[basicBrushLink] || brushIcons.error,
      };
    });
    const customBrushes = structureItems(allCustomBrushes, user.customBrushGroups, user.customBrushOrder);

    let boughtBrushes = user.boughtBrushes || [];

    // const boughtBrushesFix = Object.keys(user.brush || {})
    // .filter(brushKey=>!boughtBrushes.includes(brushKey) && user.brush?.[brushKey]?.author);
    // boughtBrushes = boughtBrushes.concat(boughtBrushesFix)

    const allMarketBrushes = boughtBrushes.map(key => {
      const brushSettings = customBrushSetsFun(key);
      const basicBrushLink = brushSettings.clone;
      return {
        type: 'brush',
        key,
        label: brushSettings.label,
        icon: brushIcons[basicBrushLink] || brushIcons.error,
      };
    });
    const marketBrushes = structureItems(allMarketBrushes, user.marketBrushGroups, user.marketBrushOrder);


    return {
      basicBrushes,
      customBrushes,
      marketBrushes,
      allBasicBrushes,
      allCustomBrushes,
      allMarketBrushes,
    };
  };

  const { basicBrushes, customBrushes, marketBrushes, allBasicBrushes, allCustomBrushes, allMarketBrushes } = getBrushes();
  const currentBrushes = 
  user.brushCat === 'market' ? marketBrushes :
  user.brushCat === 'custom' ? customBrushes :
  basicBrushes;

  const handleOverlayClick = () => {
    if (brush.lastActionTime < Date.now() - 300) {
      closeMenu();
    }
  };

  const handleCreateGroup = () => {
    showPopupMessage({
      title: t('brush.group.create_title'),
      text: t('brush.group.create_text'),
      input: {
        placeholder: t('brush.group.create_placeholder'),
      },
      buttons: [
        { type: 'cancel', text: t('customize.button_cancel') },
        { id: 'submit', type: 'submit', text: t('customize.button_submit') },
      ],
      callback: (buttonId, inputValue) => {
        if (buttonId === 'submit' && inputValue) {
          window.croco?.haptic?.();
          const category = getBrushCategory();
          const groupsKey = `${category}BrushGroups`;
          const brushOrderKey = `${category}BrushOrder`;
          const groups = user[groupsKey] || {};
          const currentOrder = user[brushOrderKey] || [];
          
          const newGroupId = new ObjectID().toHexString();
          const newGroup = {
            title: inputValue,
            expanded: true,
          };

          // Добавляем новую группу в начало порядка
          const newOrder = [newGroupId, ...currentOrder];

          saveUserSets({
            [groupsKey]: { ...groups, [newGroupId]: newGroup },
            [brushOrderKey]: newOrder
          });
          menu.methods.render();
        }
      }
    });
  };

  const handleToggleGroup = (groupId) => {
    const category = getBrushCategory();
    const groupsKey = `${category}BrushGroups`;
    const groups = { ...(user[groupsKey] || {}) };

    if (groups[groupId]) {
      groups[groupId] = {
        ...groups[groupId],
        expanded: !groups[groupId].expanded,
      };
      saveUserSets({ [groupsKey]: groups });
      menu.methods.render();
    }
  };

  const handleSwitchBrush = (newBrushType) => {
    if (brush.lastActionTime > Date.now() - 300) return;

    if (!freeBrushes.includes(newBrushType) && 
        (!premiumFeatures.includes('brushes') || user.premiumOff)) {
      closeMenu();
      delayPremiumMenu('menu');
      return;
    }

    if (brushType !== newBrushType) {
      window.croco?.appleHaptic('medium');
      window.croco?.androidHaptic();
    }

    changeBrushType(newBrushType);
    if (newBrushType !== 'spray') {
      useStore.setState({ softness: 0 });
    }
    brush.brushHistory.push(newBrushType);
    closeMenu();
  };

  const handleOpenBrushSettings = (newBrushType, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    brush.lastActionTime = Date.now();
    openMenu('customizeBrush', { brush: newBrushType, layer: true });

    if (newBrushType !== 'plain' && 
        (!premiumFeatures.includes('brushes') || user.premiumOff)) {
      return;
    }

    if (newBrushType !== 'spray') {
      useStore.setState({ softness: 0 });
    }

    if (newBrushType !== 'common') {
      brush.brushHistory.push(newBrushType);
      changeBrushType(newBrushType);
    }
  };

  const getBrushCategory = () => {
    if (user.brushCat === 'custom') return 'custom';
    if (user.brushCat === 'market') return 'market';
    return 'basic';
  }

  const handleDeleteItem = (itemId) => {
    const category = getBrushCategory();

    const item = currentBrushes.find(i => i.key === itemId);
    if (!item) return;

    const isUserBrush = user.savedBrushes?.includes(item.key);
    const isMarketBrush = user.boughtBrushes?.includes(item.key);

    if (item.type === 'brush' && !isUserBrush && !isMarketBrush) {
      showTemporaryHint(t('brush.group.cannot_delete_basic'));
      return;
    }

    const performDelete = () => {

      if (item.type === 'group') {
        const groupsKey = `${category}BrushGroups`;
        const currentGroups = { ...(user[groupsKey] || {}) };
        delete currentGroups[item.key];
        saveUserSets({ [groupsKey]: currentGroups });

      } else if (item.type === 'brush') {

        user.savedBrushes ??= [];
        user.boughtBrushes ??= [];

        user.savedBrushes = user.savedBrushes.filter(key=>key !== item.key);
        user.boughtBrushes = user.boughtBrushes.filter(key=>key !== item.key);

        info.brush[item.key] = null;
        deleteBrush(item.key);

      }
      menu.methods.render();
      window.croco?.haptic?.();
    };
    
    showPopupMessage({
      title: t('customize.delete_brush_title', { label: item.label || item.title }),
      text: item.type === 'group' ? t('customize.delete_group_text', { label: item.label || item.title }) : t('customize.delete_brush_text', { label: item.label || item.title }),
      buttons: [
        { type: 'cancel', text: t('customize.button_cancel') },
        { id: 'delete', type: 'destructive', text: t('customize.button_delete_confirm') },
      ],
      callback: (buttonId) => {
        if (buttonId === 'delete') {
          performDelete();
        }
      },
    });
  };

  const onSort = (sortedItems) => {
    // Это предотвратит срабатывание клика и закрытие меню
    brush.lastActionTime = Date.now();

    const category = getBrushCategory();
    const brushOrderKey = `${category}BrushOrder`;

    const finalBrushOrder = sortedItems.map(i => i.key);

    saveUserSets({
      [brushOrderKey]: finalBrushOrder,
    });

    menu.methods.render();
    menu.methods.appleHaptic?.('light');

  };

  const onMove = (draggedId, targetId) => {
    // Это предотвратит срабатывание клика и закрытие меню
    brush.lastActionTime = Date.now();

    const targetItem = currentBrushes.find(i => i.key === targetId);
    const draggedItem = currentBrushes.find(i => i.key === draggedId);
   
    // Перемещение кисти в группу
    if (draggedItem?.type === 'brush' && targetItem?.type === 'group') {
      
      const brushSettings = customBrushSetsFun(draggedId);
      brushSettings.groupId = targetId;
      saveBrush({ [draggedId]: brushSettings });

      setTimeout(() => menu.methods.render(), 50);
      return true; // Сообщаем хуку, что мы обработали событие
    }

    // Перемещение кисти к другой кисти (наследует ее groupId)
    if (draggedItem?.type === 'brush' && targetItem?.type === 'brush') {
      const category = getBrushCategory();
      const groupsKey = `${category}BrushGroups`;
      const groups = { ...(user[groupsKey] || {}) };
      
      const targetBrushSettings = customBrushSetsFun(targetId);
      const targetGroupId = targetBrushSettings.groupId || null;
      
      // Если целевая кисть в группе, разворачиваем эту группу
      if (targetGroupId && groups[targetGroupId] && !groups[targetGroupId].expanded) {
        groups[targetGroupId] = { ...groups[targetGroupId], expanded: true };
      }
      
      const draggedBrushSettings = customBrushSetsFun(draggedId);
      draggedBrushSettings.groupId = targetGroupId;
      saveBrush({ [draggedId]: draggedBrushSettings });

      if (targetGroupId) {
        saveUserSets({ [groupsKey]: groups }); 
      }
      setTimeout(() => menu.methods.render(), 50);
      return false; 
    }

    return false; // Мы не обрабатываем это событие, хук должен заняться сортировкой
  };

  const { draggingId, dragOverId, dragDirection, isDraggingActive, handlePointerDownToStartDrag, itemRefs } = useDragAndDropSort({
    items: currentBrushes,
    onSort,
    onMove,
    containerRef: brushMenuRef,
    keyExtractor: (item) => item.key,
    deleteZoneRef,
    onDelete: handleDeleteItem,
    setIsOverDeleteZone,
    needToStopPropagation: true,
  });

  const isBrushInGroup = (oneItem) => {
    if (oneItem.type !== 'brush') return false;
    const brushSettings = customBrushSetsFun(oneItem.key);
    if (!brushSettings.groupId) return false;

    const category = getBrushCategory();
    const groupsKey = `${category}BrushGroups`;
    const groups = user[groupsKey] || {};
    return !!groups[brushSettings.groupId];
  };

  return (
    <>
      <div className="overlay-background" onClick={handleOverlayClick}></div>
      <div className="brush-menu">

        <div className="brush-menu-header">
           <button className="brush-menu-action-btn" onClick={handleCreateGroup}>
             <FaFolderPlus />
           </button>
           <span 
              className="brush-menu-title"
              {...createBetterClick({
                onClick: (e) => handleOpenBrushSettings('common', e),
                onContextMenu: (e) => handleOpenBrushSettings('common', e),
              })}
            >
             {t('brush.title')}
             <button className="brush-menu-action-btn" onClick={(e) => handleOpenBrushSettings('common', e)}>
             <FaCog style={{ paddingLeft: '10px', }} />
           </button>
           </span>
           <div className="brush-menu-action-btn">
             <div 
                ref={deleteZoneRef} 
                className={`delete-drop-zone ${isOverDeleteZone ? 'active' : ''} ${isDraggingActive ? 'visible' : ''}`}
             >
                <FaTrash/>
             </div>
           </div>
         </div>

        <div className="brush-menu-list add-scroll touch-active" ref={brushMenuRef}>
          {currentBrushes.map((oneItem) => (
             oneItem.type === 'group' ? (
              <div
                key={oneItem.key}
                ref={el => (itemRefs.current[oneItem.key] = el)}
                data-draggable-id={oneItem.key}
                className={`brush-group-row ${isDraggingActive && draggingId === oneItem.key ? 'dragging' : ''} ${dragOverId === oneItem.key && currentBrushes.find(i=>i.key === draggingId)?.type === 'brush' ? 'drag-over-group' : ''} ${dragOverId === oneItem.key && !(currentBrushes.find(i=>i.key === draggingId)?.type === 'brush') ? `drag-over drag-over-${dragDirection}` : ''} ${oneItem.expanded ? 'expanded' : ''}`}
                onClick={() => handleToggleGroup(oneItem.key)}
              >
                <span className="brush-group-toggle">
                  {oneItem.expanded ? <FaMinus /> : <FaPlus />}
                </span>
                <span className="brush-group-label">
                  {oneItem.title}
                  <span className="brush-group-label-count">
                    ({brushesInGroupsCount()[oneItem.key] || 0})
                  </span>
                </span>
                 <div className="brush-drag-handle" style={{fontSize: '18px'}} onPointerDown={(e) => handlePointerDownToStartDrag(e, oneItem.key)} onClick={(e) => e.stopPropagation()}>
                  ☰
                </div>
              </div>
             ) : (
            <div
              key={oneItem.key}
              ref={el => (itemRefs.current[oneItem.key] = el)}
              data-draggable-id={oneItem.key}
              className={`brush-option-row ${isBrushInGroup(oneItem) ? 'in-group' : ''} ${brushItemAvailabilityStyle(oneItem.key)} ${isDraggingActive && draggingId === oneItem.key ? 'dragging' : ''} ${dragOverId === oneItem.key ? `drag-over drag-over-${dragDirection}` : ''}`}
              {...createBetterClick({
                onClick: () => handleSwitchBrush(oneItem.key),
                onContextMenu: (e) => handleOpenBrushSettings(oneItem.key, e),
              })}
            >
              <label className="brush-label">
                <oneItem.icon className={`brush-icon ${brushItemAvailabilityStyle(oneItem.key)}`} />
                <span> {oneItem.label}</span>
                {brushType === oneItem.key && (
                  <FaCog
                    className="brush-settings-icon"
                    {...createBetterClick({
                      onClick: (e) => handleOpenBrushSettings(oneItem.key, e),
                      onContextMenu: (e) => handleOpenBrushSettings(oneItem.key, e),
                    })}
                  />
                )}
              </label>
               <div className="brush-drag-handle" onPointerDown={(e) => handlePointerDownToStartDrag(e, oneItem.key)} onClick={(e) => e.stopPropagation()} onContextMenu={(e) => {e.preventDefault(); e.stopPropagation()}}>
                  ☰
                </div>
            </div>
             )
           ))}
        </div>

        {/* Переключатель категорий */}
        <div className="brush-category-switcher">
          <button
            className={`category-btn ${!['custom', 'market'].includes(user.brushCat) ? 'active' : ''}`}
            onClick={() => saveUserSets({brushCat: 'basic'})}
          >
            {t('brush.category.basic')}
          </button>
          <button
            className={`category-btn ${user.brushCat === 'custom' ? 'active' : ''}`}
            onClick={() => saveUserSets({brushCat: 'custom'})}
          >
            {t('brush.category.custom')}
          </button>
          { marketBrushes.length > 0 && (
            <button
            className={`category-btn ${user.brushCat === 'market' ? 'active' : ''}`}
            onClick={() => saveUserSets({brushCat: 'market'})}
          >
            {t('brush.category.market')}
          </button>
          )}
        </div>
      </div>
    </>
  );
}

export default BrushMenu;
